/**
 * Drive circle-junction turntable + H-connector animations during backend
 * playback.
 *
 * Vehicle motion comes from the backend timeline (per-OHT delayed time after
 * junction waits). When the stream publishes `junction_wait` dwells, those legs
 * time the disk: it turns toward the exit inside the streamed window and the
 * OHT moves off as the window closes. Otherwise circle junctions hold every OHT
 * at the hub for `waitSec` (API) / `junctionWaitSec`, then rotate the disk, then
 * release without a timeline jump. H-connectors still use wait→close→shift pose
 * overrides.
 */
import { useEffect, useMemo, useRef } from 'react';
import { updateElement } from '../features/layout/layoutSlice';
import { connectorShiftStore } from '../store/connectorShiftStore';
import {
  junctionTurntableStore,
  shortestAngleDeltaDeg,
} from '../store/junctionTurntableStore';
import { playbackDelayStore } from '../store/playbackDelayStore';
import {
  railTransferPoseStore,
  type RailTransferPose,
} from '../store/railTransferPoseStore';
import { simulationClockStore } from '../store/simulationClockStore';
import { simulationFrameBus } from '../store/simulationFrameBus';
import { tickConnectorShifts } from '../utils/connectorShiftSim';
import {
  buildAuthoritativeJunctionSegmentPortMap,
  hasAuthoritativeJunctionKinematics,
  isTurntableStraightThroughMove,
  tickTurntableAlignments,
  type AuthoritativeJunctionSegmentPortMap,
} from '../utils/junctionTurntableSim';
import {
  backendTimedJunctionOhtIds,
  resolveStreamedJunctionTurns,
} from '../utils/streamedJunctionWaits';
import {
  getTurntableHubWorld,
  getTurntableSettledAngleDeg,
} from '../utils/junctionTurntableSim';
import { junctionTurnOhtAngleDeg } from '../utils/streamedJunctionWaits';
import type {
  BrowserKineticVehicleLeg,
  VisualizationEvent,
} from '../types/simulationPlayback';
import { ALL_RAIL_TYPES } from '../utils/topology';
import { getPlaybackPosition, lerpAngleDeg } from '../utils/simulationPlayback';
import { liveOhtRuntimeStore } from '../store/liveOhtRuntimeStore';
import { metersToEffectiveWorldUnits, shouldUseDxfCalibration } from '../utils/measurement';
import { useAppDispatch, useAppSelector } from './redux';

/** How far ahead (sim seconds) we peek to detect entry→exit turns. */
const TURNTABLE_LOOKAHEAD_S = 1.5;
/** Soft handoff after connector land so OHT doesn't teleport back to path. */
const CONNECTOR_HANDOFF_S = 0.45;
/** Soft handoff after turntable release so hub pin → path isn't a snap. */
const TURNTABLE_HANDOFF_S = 0.35;

type PoseHandoff = {
  x: number;
  y: number;
  angle: number;
  startS: number;
};

/** Runs turntable + H-connector transfer animations alongside backend playback. */
export function useRailTransferAnimations(): void {
  const dispatch = useAppDispatch();
  const running = useAppSelector((state) => state.simulation.running);
  const elements = useAppSelector((state) => state.layout.elements);
  const playbackVehicles = useAppSelector((state) => state.simulation.playbackVehicles);
  const replayPlan = useAppSelector((state) => state.simulation.replayPlan);
  const idleByVehicle = useAppSelector(
    (state) => state.simulation.browserKinetic?.vehicleLegs.idleByVehicle ?? {}
  );
  const floorPlanScaleFactor = useAppSelector((state) => state.floorplan.scaleFactor);
  const dxfUnitsPerMeter = useAppSelector((state) => state.floorplan.dxfUnitsPerMeter);
  const floorplanBounds = useAppSelector((state) => state.floorplan.bounds);
  const floorplanPolylineCount = useAppSelector((state) => state.floorplan.polylines.length);

  /** Rails, curves and junctions — the transfer ticks ignore everything else. */
  const trackElements = useMemo(
    () =>
      Object.values(elements).filter(
        (el) => ALL_RAIL_TYPES.has(el.type) && el.metadata.visible
      ),
    [elements]
  );
  const authoritativeSegmentPorts = useMemo(
    () => buildAuthoritativeJunctionSegmentPortMap(trackElements),
    [trackElements]
  );

  const trackRef = useRef(trackElements);
  const playbackRef = useRef(playbackVehicles);
  const idleRef = useRef(idleByVehicle);
  const authoritativeEventsRef = useRef<readonly VisualizationEvent[] | null>(
    replayPlan?.events ?? null
  );
  const authoritativeSegmentPortsRef = useRef<AuthoritativeJunctionSegmentPortMap>(
    authoritativeSegmentPorts
  );
  const metersToWorldRef = useRef<(m: number) => number>((m) => m * 20);
  /** Prior-frame OHT poses so turntable holds still fire when speed skips the hub. */
  const prevOhtPosRef = useRef<Array<{ id: string; x: number; y: number }>>([]);

  useEffect(() => {
    trackRef.current = trackElements;
    playbackRef.current = playbackVehicles;
    idleRef.current = idleByVehicle;
    authoritativeEventsRef.current = replayPlan?.events ?? null;
    authoritativeSegmentPortsRef.current = authoritativeSegmentPorts;
    const hasFloor = shouldUseDxfCalibration(Boolean(floorplanBounds), floorplanPolylineCount);
    metersToWorldRef.current = (meters: number) =>
      metersToEffectiveWorldUnits(
        meters,
        hasFloor ? floorPlanScaleFactor : undefined,
        hasFloor ? dxfUnitsPerMeter : undefined
      );
  }, [
    trackElements,
    playbackVehicles,
    replayPlan,
    authoritativeSegmentPorts,
    idleByVehicle,
    floorPlanScaleFactor,
    dxfUnitsPerMeter,
    floorplanBounds,
    floorplanPolylineCount,
  ]);

  useEffect(() => {
    if (!running) {
      junctionTurntableStore.reset();
      connectorShiftStore.reset();
      railTransferPoseStore.reset();
      playbackDelayStore.reset();
      prevOhtPosRef.current = [];
      return;
    }

    const connectorHandoffs = new Map<string, PoseHandoff>();
    const turntableHandoffs = new Map<string, PoseHandoff>();
    let prevTurntableHolding = new Map<string, PoseHandoff>();

    const tick = (simT: number) => {
      // Drive transfer animations from simulation time so they stay locked to
      // OHT motion at every playback speed (0.5× … 20×) and freeze when paused.
      const nowMs = simT * 1000;
      const els = trackRef.current;
      const playback = playbackRef.current;
      const idleMap = idleRef.current;

      const ohtWorld: Array<{
        id: string;
        x: number;
        y: number;
        angle: number;
        lookaheadAngle: number;
        lookaheadX: number;
        lookaheadY: number;
      }> = [];
      for (const vehicle of Object.values(playback)) {
        const idle = idleMap[vehicle.ohtId];
        if (!idle) continue;
        const t = playbackDelayStore.getEffectiveTime(vehicle.ohtId, simT);
        const pos = getPlaybackPosition(vehicle, idle, t);
        if (!Number.isFinite(pos.x) || !Number.isFinite(pos.y)) continue;
        const ahead = getPlaybackPosition(vehicle, idle, t + TURNTABLE_LOOKAHEAD_S);
        ohtWorld.push({
          id: vehicle.ohtId,
          x: pos.x,
          y: pos.y,
          angle: pos.angle,
          lookaheadAngle: ahead.angle,
          lookaheadX: ahead.x,
          lookaheadY: ahead.y,
        });
      }

      // --- Streamed junction dwells: turn the disk inside the wait window ---
      // The dwell leg holds the OHT at the node, so the disk has the whole
      // window to face the exit and the vehicle leaves the instant it closes.
      const legsByVehicle: Record<string, readonly BrowserKineticVehicleLeg[]> = {};
      for (const vehicle of Object.values(playback)) {
        legsByVehicle[vehicle.ohtId] = vehicle.legs;
      }
      const streamTimedOhtIds = backendTimedJunctionOhtIds(legsByVehicle);
      const streamedTurns = streamTimedOhtIds.size
        ? resolveStreamedJunctionTurns({
            simTimeS: simT,
            simTimeByOhtId: Object.fromEntries(
              Object.keys(legsByVehicle).map((ohtId) => [
                ohtId,
                liveOhtRuntimeStore.getOht(ohtId)?.localSimTimeS ?? simT,
              ])
            ),
            legsByVehicle,
            elements: els,
          })
        : [];
      const streamedJunctionIds = new Set(streamedTurns.map((turn) => turn.junctionId));
      for (const turn of streamedTurns) {
        const el = els.find((candidate) => candidate.id === turn.junctionId);
        if (!el) continue;
        const settled = getTurntableSettledAngleDeg(el);
        if (Math.abs(shortestAngleDeltaDeg(settled, turn.barAngleDeg)) < 0.25) continue;
        dispatch(
          updateElement({
            id: el.id,
            updates: {
              customProperties: {
                ...el.customProperties,
                turntableAngleDeg: turn.barAngleDeg,
              },
            },
          })
        );
      }

      // --- Circle-junction: every OHT waits at the hub (sim-ms × playbackSpeed) ---
      const authoritativeEvents = authoritativeEventsRef.current;
      // A compiled replay plan is always present, but it rarely carries
      // enter/rotate/clear with from+to segments. Using event mode then
      // freezes every disk. Only a complete junction stream is authoritative.
      const useAuthoritativeJunctions =
        hasAuthoritativeJunctionKinematics(authoritativeEvents);
      const turntableTick = useAuthoritativeJunctions
        ? tickTurntableAlignments({
            mode: 'authoritative-events',
            now: nowMs,
            elements: els,
            ohts: ohtWorld,
            visualizationEvents: authoritativeEvents ?? [],
            segmentPortMap: authoritativeSegmentPortsRef.current,
          })
        : tickTurntableAlignments({
            mode: 'legacy-proximity',
            now: nowMs,
            elements: els,
            ohts: ohtWorld,
            prevOhts: prevOhtPosRef.current,
            streamTimed: streamTimedOhtIds.size
              ? { ohtIds: streamTimedOhtIds, junctionIds: streamedJunctionIds }
              : null,
          });
      prevOhtPosRef.current = ohtWorld.map((o) => ({ id: o.id, x: o.x, y: o.y }));
      for (const patch of turntableTick.updates) {
        dispatch(updateElement(patch));
      }

      // --- H-connector transfers ---
      const connectorTick = useAuthoritativeJunctions
        ? { completed: [], waiting: [], riding: [] }
        : tickConnectorShifts({
            now: nowMs,
            ohtWorldPositions: ohtWorld,
            elements: els,
            metersToWorld: metersToWorldRef.current,
          });

      for (const patch of connectorTick.completed) {
        if (patch.updates && Object.keys(patch.updates).length > 0) {
          dispatch(updateElement({ id: patch.id, updates: patch.updates }));
        }
        if (patch.ohtId && patch.landAt) {
          connectorHandoffs.set(patch.ohtId, {
            x: patch.landAt.x,
            y: patch.landAt.y,
            angle: patch.landAt.angle,
            startS: simT,
          });
        }
      }

      // Pose overrides: turntable holds + connector wait/ride (connector wins on conflict).
      const nextPoses = new Map<string, RailTransferPose>();
      const nextTurntableHolding = new Map<string, PoseHandoff>();
      for (const pose of turntableTick.holding) {
        nextPoses.set(pose.ohtId, {
          x: pose.x,
          y: pose.y,
          angle: pose.angle,
          kind: 'turntable',
        });
        nextTurntableHolding.set(pose.ohtId, {
          x: pose.x,
          y: pose.y,
          angle: pose.angle,
          startS: simT,
        });
        turntableHandoffs.delete(pose.ohtId);
      }
      // Streamed junction dwells rotate the disk via Redux but skip the legacy
      // boarded hold — pin the OHT at the hub with a matching heading anyway.
      for (const turn of streamedTurns) {
        const el = els.find((candidate) => candidate.id === turn.junctionId);
        if (!el) continue;
        if (
          isTurntableStraightThroughMove(
            el,
            turn.fromPort,
            turn.toPort,
            els
          )
        ) {
          continue;
        }
        const hub = getTurntableHubWorld(el);
        const angle = junctionTurnOhtAngleDeg(
          el,
          turn.fromPort,
          turn.toPort,
          turn.progress01,
          els
        );
        nextPoses.set(turn.ohtId, {
          x: hub.x,
          y: hub.y,
          angle,
          kind: 'turntable',
        });
        nextTurntableHolding.set(turn.ohtId, {
          x: hub.x,
          y: hub.y,
          angle,
          startS: simT,
        });
        turntableHandoffs.delete(turn.ohtId);
      }
      // Hub pin ended → blend toward delayed playback path (no teleport).
      for (const [ohtId, prev] of prevTurntableHolding) {
        if (nextTurntableHolding.has(ohtId) || nextPoses.has(ohtId)) continue;
        // CLEAR is the authoritative release edge, but the event-defined route
        // may already be a few samples beyond the hub. Blend from the held pose
        // instead of dropping the override and teleporting across the junction.
        turntableHandoffs.set(ohtId, { ...prev, startS: simT });
      }
      prevTurntableHolding = nextTurntableHolding;

      for (const pose of [...connectorTick.waiting, ...connectorTick.riding]) {
        nextPoses.set(pose.ohtId, {
          x: pose.x,
          y: pose.y,
          angle: pose.angle,
          kind: 'connector',
        });
        connectorHandoffs.delete(pose.ohtId);
        turntableHandoffs.delete(pose.ohtId);
      }

      const blendHandoff = (
        handoffs: Map<string, PoseHandoff>,
        durationS: number,
        kind: RailTransferPose['kind']
      ) => {
        for (const [ohtId, handoff] of [...handoffs]) {
          if (nextPoses.has(ohtId)) continue;
          const elapsed = simT - handoff.startS;
          if (elapsed >= durationS) {
            handoffs.delete(ohtId);
            continue;
          }
          const vehicle = Object.values(playback).find((v) => v.ohtId === ohtId);
          const idle = idleMap[ohtId];
          if (!vehicle || !idle) {
            handoffs.delete(ohtId);
            continue;
          }
          const pathT = playbackDelayStore.getEffectiveTime(ohtId, simT);
          const path = getPlaybackPosition(vehicle, idle, pathT);
          const u = Math.min(1, Math.max(0, elapsed / durationS));
          const s = u * u * (3 - 2 * u);
          nextPoses.set(ohtId, {
            x: handoff.x + (path.x - handoff.x) * s,
            y: handoff.y + (path.y - handoff.y) * s,
            angle: lerpAngleDeg(handoff.angle, path.angle, s),
            kind,
          });
        }
      };

      blendHandoff(turntableHandoffs, TURNTABLE_HANDOFF_S, 'turntable');
      blendHandoff(connectorHandoffs, CONNECTOR_HANDOFF_S, 'connector');

      railTransferPoseStore.replaceAll(nextPoses);
    };

    // Run before paint phase so pose overrides exist in the same frame.
    const unsubscribe = simulationFrameBus.subscribe((simTime) => {
      tick(simTime);
    }, 'transfers');

    tick(simulationClockStore.getTime());

    return () => {
      unsubscribe();
    };
  }, [running, dispatch]);
}
